import { useRef, useState } from 'react';
import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import { FileDownloadOutlined, FileUploadOutlined } from '@mui/icons-material';
import {
  intakeWorkbookUrl, analyzeIntakeImport, type ImportPreview,
} from '../../../../services/publicIntakeService';
import type { IntakeFormDef, PublicIntakeSample } from '../../../../types/intake';
import { ImportReviewDialog } from '../../../../components/intakeImport/ImportReviewDialog';

interface Props {
  token: string;
  def: IntakeFormDef;
  /** The confirmed import — parent applies it via the normal (undoable) edit. */
  onApply: (result: { fields: Partial<Record<string, unknown>>; samples: PublicIntakeSample[] }) => void;
  /**
   * 'hero' — the headline card at the top of an EMPTY samples step (import is
   * the preferred path: field crews always have an Excel). 'compact' — the
   * small utility row next to the nav buttons (default).
   */
  variant?: 'compact' | 'hero';
}

/**
 * The offline Excel round-trip: download our branded workbook (prefilled
 * with the current draft), fill it offline, import it back. Import also
 * accepts a filled LEGACY KeyLAB request form — either way the file is
 * analyzed first and applied only after the review dialog is confirmed.
 */
export const ExcelRoundTrip = ({ token, def, onApply, variant = 'compact' }: Props) => {
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File | null) => {
    if (!file) return;
    setAnalyzing(true);
    setError(null);
    try {
      setPreview(await analyzeIntakeImport(token, file));
    } catch (err) {
      setError((err as Error).message || 'Could not read this file.');
    } finally {
      setAnalyzing(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const fileInputEl = (
    <input
      ref={fileInput} type="file" hidden
      accept=".xlsx,.xlsm,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12,application/vnd.ms-excel"
      onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
    />
  );

  return (
    <Box sx={variant === 'hero' ? { width: '100%' } : undefined}>
      {variant === 'hero' ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#EEF2E6', borderColor: '#4A6741' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Working from a field Excel file? Import it here.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Most requests start from a spreadsheet — import your filled workbook (or an old
            KeyLAB request form) and edit the result below. Nothing is applied until you
            review and confirm it.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="contained" startIcon={<FileUploadOutlined />}
              onClick={() => fileInput.current?.click()} disabled={analyzing}
              sx={{ textTransform: 'none' }}
            >
              {analyzing ? 'Reading…' : 'Import Excel file'}
            </Button>
            <Button
              variant="outlined" startIcon={<FileDownloadOutlined />}
              href={intakeWorkbookUrl(token)} sx={{ textTransform: 'none' }}
            >
              Download our blank workbook
            </Button>
          </Box>
          {fileInputEl}
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">Prefer Excel?</Typography>
          <Button
            size="small" startIcon={<FileDownloadOutlined sx={{ fontSize: 16 }} />}
            href={intakeWorkbookUrl(token)}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            Download workbook
          </Button>
          <Button
            size="small" startIcon={<FileUploadOutlined sx={{ fontSize: 16 }} />}
            onClick={() => fileInput.current?.click()}
            disabled={analyzing}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            {analyzing ? 'Reading…' : 'Import filled workbook'}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7 }}>
            (old KeyLAB request files work too)
          </Typography>
          {fileInputEl}
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mt: 1, py: 0.25 }} onClose={() => setError(null)}>{error}</Alert>}

      <ImportReviewDialog
        open={preview !== null}
        preview={preview}
        def={def}
        onCancel={() => setPreview(null)}
        onConfirm={(result) => {
          setPreview(null);
          onApply(result);
        }}
      />
    </Box>
  );
};
